import type { EstipulanteUsuarioContext } from './estipulanteVinculoType';
import type { UserPoliciesListItem, UserPoliciesListResponse } from './serviceType';
import type { ListaSubestipulanteItem } from './vida/subEstipulante/subEstipulanteType';

// Sort item interface for pagination
export interface SortItem {
  direction: string;
  property: string;
  ignoreCase: boolean;
  nullHandling: string;
  ascending: boolean;
}

/**
 * Policy fields returned by GET `/v1/bff/vida/apolice/listar-apol-perf-usuario`.
 * Set on CompanyPage after business-link confirmation.
 */
export interface CompanyApoliceContext {
  identContratoApol?: number;
  /** Some responses only include `identContrato` */
  identContrato?: number | null;
  identEstipulanteApol?: number;
  numApoliceApol?: number;
  codEmpresaApol?: number;
  codSucursalApol?: number;
  codRamoApol?: number;
  codprodutoApol?: number;
  numDocumentoApol?: string;
}

/**
 * Selected policy snapshot in `companyStore` after CompanyPage confirmation.
 * Only BFF policy/context data — no separate “company” entity. Labels for UI use {@link getSelectedCompanyEmpresaDisplayLabel} / {@link getSelectedCompanyPolicyLabel} / {@link getSelectedCompanyRamoLine}.
 */
export interface Company {
  /** `VinculoNegocio.idVinculoNegocio` */
  id: string;
  apoliceContext?: CompanyApoliceContext;
  policySelection?: EstipulanteUsuarioContext;
  /** GET obter-lista-subestipulantes após confirmação da apólice (CompanyPage). */
  subestipulantes?: ListaSubestipulanteItem[];
}

/** `identContrato` query param for obter-lista-subestipulantes (BFF policy row). */
export function resolveIdentContratoFromPolicyRow(row: UserPoliciesListItem): number | undefined {
  const raw = row.identContrato ?? row.identContratoApol;
  if (raw == null) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Navbar / sidebar one-line label, e.g. `Apólice 12345`. */
export function getSelectedCompanyPolicyLabel(company: Company | null): string {
  const ps = company?.policySelection;
  if (!ps) return '';
  const n = String(ps.numeroApolice ?? '').trim();
  return n ? `Apólice ${n}` : '';
}

function pickPolicyRowFromPoliciesList(
  listRes: UserPoliciesListResponse | null | undefined,
  numeroApolice: string,
): UserPoliciesListItem | undefined {
  const rows =
    listRes?.flatMap((g) => g.retorno ?? []).filter((x): x is UserPoliciesListItem => x != null) ?? [];
  if (rows.length === 0) return undefined;
  const numApoliceNum = Number(String(numeroApolice).replace(/\D/g, ''));
  if (Number.isFinite(numApoliceNum) && numApoliceNum > 0) {
    const match = rows.find((r) => {
      if (r.numApoliceApol == null) return false;
      const n = Number(String(r.numApoliceApol).replace(/\D/g, ''));
      return Number.isFinite(n) && n === numApoliceNum;
    });
    if (match) return match;
  }
  return rows[0];
}

/**
 * Identifier to do estipulante in context of the policy (Vida / BFF `idEstipulante`).
 * Uses the line of {@link UserPoliciesListItem} aligned with `numeroApolice`; if missing, `apoliceContext`.
 */
export function getSelectedPolicyIdentEstipulanteApol(company: Company | null): number | undefined {
  const ps = company?.policySelection;
  if (ps?.policiesListResponse?.length) {
    const row = pickPolicyRowFromPoliciesList(ps.policiesListResponse, String(ps.numeroApolice ?? ''));
    if (row?.identEstipulanteApol != null) {
      const n = Number(row.identEstipulanteApol);
      if (Number.isFinite(n)) return n;
    }
  }
  const ctx = company?.apoliceContext?.identEstipulanteApol;
  if (ctx != null) {
    const n = Number(ctx);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/**
 * Code of the subestipulante in the policy row (for `jsonApoliceSub` / SUB-ESTIPULANTE profile).
 */
export function getSelectedPolicyCdSubEstipulanteApol(company: Company | null): string | undefined {
  const ps = company?.policySelection;
  if (ps?.policiesListResponse?.length) {
    const row = pickPolicyRowFromPoliciesList(ps.policiesListResponse, String(ps.numeroApolice ?? ''));
    if (row?.cdSubEstipulanteApol != null) {
      return String(row.cdSubEstipulanteApol);
    }
  }
  return undefined;
}

/** `nomeEmpresaApol` Line of business name in the policy row in `policySelection.policiesListResponse` (same criteria as in CompanyPage). */
export function getSelectedCompanyNomeEmpresaApol(company: Company | null): string {
  const ps = company?.policySelection;
  if (!ps?.policiesListResponse?.length) return '';
  const row = pickPolicyRowFromPoliciesList(ps.policiesListResponse, String(ps.numeroApolice ?? ''));
  return row?.nomeEmpresaApol?.trim() ?? '';
}

/** `nomeEstipulanteApol` in the selected policy row in `policySelection.policiesListResponse`. */
export function getSelectedCompanyNomeEstipulanteApol(company: Company | null): string {
  const ps = company?.policySelection;
  if (!ps?.policiesListResponse?.length) return '';
  const row = pickPolicyRowFromPoliciesList(ps.policiesListResponse, String(ps.numeroApolice ?? ''));
  return row?.nomeEstipulanteApol?.trim() ?? '';
}

/** Company name in the policy for navbar/sidebar; if no `nomeEmpresaApol`, uses the policy label. */
export function getSelectedCompanyEmpresaDisplayLabel(company: Company | null): string {
  return getSelectedCompanyNomeEmpresaApol(company) || getSelectedCompanyPolicyLabel(company) || '—';
}

export function hasSelectedCompanyPolicyNumber(company: Company | null): boolean {
  return Boolean(String(company?.policySelection?.numeroApolice ?? '').trim());
}

/** Estipulante/empresa names from `listar-apol-perf-usuario` row (not only `numeroApolice`). */
export function hasSelectedCompanyPolicyDetails(company: Company | null): boolean {
  return Boolean(
    getSelectedCompanyNomeEstipulanteApol(company) || getSelectedCompanyNomeEmpresaApol(company),
  );
}

export function isBackofficePolicySelection(company: Company | null): boolean {
  const papel = String(company?.policySelection?.papelDescricao ?? '').trim().toUpperCase();
  return papel === 'BACKOFFICE' || papel.startsWith('BACKOFFICE');
}

/** Width of the green badge in the sidebar (`w-[140px]` + `text-xs`). */
export const EMPRESA_DISPLAY_MAX_SIDEBAR = 18;

/** Width of the trigger of `CompanyCard` in the navbar (`w-[200px]` + `text-sm`). */
export const EMPRESA_DISPLAY_MAX_NAVBAR = 15;

export function truncateEmpresaDisplayLabel(text: string, maxLen: number): string {
  const t = text.trim();
  if (t.length === 0) return t;
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen)}…`;
}

/** Line-of-business line for menu enablement, e.g. `Vida (4)`. */
export function getSelectedCompanyRamoLine(company: Company | null): string {
  const ps = company?.policySelection;
  if (!ps?.ramoNome) return '';
  return `${ps.ramoNome} (${ps.ramoCodigo})`;
}

// Pagination request types
export interface CompaniesPaginationRequest {
  page: number;
  size: number;
  sort: string[];
}

// Pagination response types
/** Spring page from GET `v1/bff/companies`; cada item de `content` segue {@link UserPoliciesListItem} (mesmo formato de linha de `retorno` em listar-apol-perf-usuario). */
export interface CompaniesPaginationResponse {
  totalPages: number;
  totalElements: number;
  first: boolean;
  last: boolean;
  size: number;
  content: UserPoliciesListItem[];
  number: number;
  numberOfElements: number;
  pageable: {
    offset: number;
    pageSize: number;
    pageNumber: number;
    paged: boolean;
    sort: SortItem[];
    unpaged: boolean;
  };
  sort: SortItem[];
  empty: boolean;
}

// Company Card Component
export interface CompanyCardProps {
  company: Company | null;
}
